import styled, { css, keyframes } from 'styled-components';
import { Common, color, Widget } from '@stockbit/ui';
import { Button } from 'antd';

const { Flex, Input: InputDefault, Text } = Common;
const {
  HumbergerButton: HumbergerButtonDefault,
  Modal: WidgetModal,
  Table,
} = Widget;

const cellType = {
  up: color.primary,
  down: color.sunsetOrange,
  neutral: color.suvaGrey,
  default: color.highempahis,
};

const HamburgerButton = styled(HumbergerButtonDefault)`
  ${({ active }) =>
    active &&
    css`
      background: ${color.light};
      border-left: 1px solid ${color.gainsboro};
    `}
`;

const HeaderContainer = styled(Flex).attrs({ align: 'center', pl: '10px' })`
  position: relative;
  background: ${color.aliceBlue};
  height: 40px;
  cursor: pointer;
  border-bottom: 1px solid ${color.gainsboro};

  & .ant-select-dropdown {
    top: 4px !important;
    z-index: 1;
  }
`;

const Input = styled(InputDefault)
  .withConfig({ shouldForwardProp: (props) => !['active'].includes(props) })
  .attrs((props) => ({
    active: props.active,
  }))`
  transition : 0.5s ease;
  ${({ active }) => {
    if (active) {
      return css`
        width: 60% !important;
        padding: 4px 11px;
        border: 1px solid ${color.gainsboro};
        position: absolute;
        right: 10px;
        bottom: 5px;
      `;
    }
    return css`
      position: absolute;
      width: 0px !important;
      padding: 0;
      border: none;
      right: 58px;
      bottom: 5px;
      background: ${color.gainsboro};
    `;
  }}
`;

const ListContainer = styled(Flex).attrs({ align: 'center' })`
  border-bottom: 1px solid ${color.aliceBlue};
  cursor: pointer;

  :hover {
    background: ${color.aliceBlue};
  }
`;

const Tag = styled(Text).attrs({
  color: color.light,
  weight: 'bold',
  px: '3px',
})`
  background: ${color.dimgray};
  border-radius: 2px;
`;

const Td = styled(Text).attrs((props) => ({
  height: props.height || '30px',
  padding: props.padding || '0 10px',
  size: props.size || '12.5px',
  type: props.type || 'default',
}))`
  cursor: pointer;

  ${({ type }) =>
    css`
      color: ${cellType[type]};
    `}
`;

const Th = styled(Td).attrs({
  family: 'bold',
  size: '11px',
})`
  background-color: ${color.light};
`;

const Modal = styled(WidgetModal)`
  .ant-modal-header {
    border-bottom: none;
  }

  .ant-input-suffix {
    color: #cbcbcb;
  }
`;

const CompanyWatchlistTable = styled(Table)`
  .ant-table {
    table {
      padding: 0 12px;
      width: 100%;
    }

    .ant-table-thead {
      > tr {
        > th:first-child {
          width: 25%;
          max-width: 25%;
        }

        > th:nth-child(2) {
          width: 30%;
        }

        > th:last-child {
          width: 45%;
        }
      }
    }

    .ant-table-tbody {
      > tr {
        position: static;
        cursor: pointer;

        > td:first-child {
          width: 25%;
          max-width: 25%;
        }

        > td:nth-child(2) {
          text-align: right;
          width: 30%;
        }

        > td:last-child {
          text-align: right;
          width: 45%;
        }
      }

      > tr.selected-row {
        background: ${color.whisper};
      }
    }

    .ant-table-cell-ellipsis {
      overflow: unset;
    }
  }
`;

const DELETE_OPTION_WIDTH = '301px';

const deleteButtonAnimation = keyframes`
  0% {
    transform: translateX(-12px);
    opacity: 0;
    width: calc(${DELETE_OPTION_WIDTH} + 36px);
  }
  100% {
    transform: translateX(0);
    opacity: 1;
    width: ${DELETE_OPTION_WIDTH};
  }
`;

const DeleteOptionWrapper = styled(Flex).attrs((props) => ({
  margin: '0 auto',
  align: 'center',
  pl: '5px',
  width: props.width || DELETE_OPTION_WIDTH,
  maxWidth: props.width || DELETE_OPTION_WIDTH,
}))`
  animation: 0.2s ease-out forwards ${deleteButtonAnimation};
  background: ${color.gainsboro};
  height: 31px;
  overflow: hidden;
  position: absolute;
  text-overflow: ellipsis;
  top: -1px;
  white-space: nowrap;
  z-index: 1;
`;

const buttonType = {
  sunsetOrange: css`
    background: ${color.sunsetOrange};
    border-color: ${color.sunsetOrange};
    color: ${color.light};
    :hover,
    :focus,
    :active {
      background: ${color.sunsetOrange};
      border-color: ${color.sunsetOrange};
    }
  `,
  highempahis: css`
    background: ${color.highempahis};
    border-color: ${color.highempahis};
    color: ${color.light};
    :hover,
    :focus,
    :active {
      background: ${color.highempahis};
      border-color: ${color.highempahis};
    }
  `,
};

const ButtonWidget = styled(Button)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['round', 'active', 'borderLess'].includes(prop),
  })
  .attrs((props) => ({
    height: props.height || 'auto',
    type: props.type || 'default',
    round: props.round,
    active: props.active,
    borderLess: props.borderLess,
    margin: props.margin || '0 !important',
    padding: props.padding || '4px 15px',
  }))`
    border-radius: ${({ round }) => (round ? '2px' : '0px')};
    height: ${({ height }) => height};
    margin: ${({ margin }) => margin};
    padding: ${({ padding }) => padding};
    
    ::after{
      content: unset;
    }
    
    ${({ type }) => buttonType[type]};
    border : ${({ borderLess }) => borderLess && 'none'};
`;

export {
  HamburgerButton,
  HeaderContainer,
  Input,
  ListContainer,
  Tag,
  Td,
  Th,
  Modal,
  CompanyWatchlistTable,
  DeleteOptionWrapper,
  ButtonWidget,
};
